import { VALID_COUNTRY_IDS } from './countryConfig';

const PREFIX = 'barcode:add-recovery:v1:';
export const RECOVERY_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const validId = id => typeof id === 'string' && id.length > 0 && id.length <= 1500 && !id.includes('/');
export const recoveryKey = (uid, country) => {
  if (!validId(uid) || !VALID_COUNTRY_IDS.includes(country)) throw new Error('無法辨識保存帳號或國家');
  return `${PREFIX}${encodeURIComponent(uid)}:${country}`;
};
const invalid = () => new Error('本機保存紀錄無法辨識。為避免重複新增，請先從商品列表確認，不會建立新商品。');

// Only IDs and lifecycle metadata are persisted. Never store form fields,
// photos, names, credentials, or Firebase tokens in this recovery record.
export const readCreationRecovery = (uid, country) => {
  let raw;
  try { raw = localStorage.getItem(recoveryKey(uid, country)); } catch (_) { throw new Error('無法讀取本機保存紀錄，暫停新增以避免重複商品'); }
  if (!raw) return null;
  let record;
  try { record = JSON.parse(raw); } catch (_) { throw invalid(); }
  if (record.version !== 1 || record.uid !== uid || record.country !== country || !validId(record.productId)
    || !['pending', 'saved', 'complete'].includes(record.phase) || !Number.isSafeInteger(record.revision)
    || !Number.isFinite(record.updatedAt) || !Array.isArray(record.choices) || record.choices.length > 100
    || record.choices.some(c => !validId(c.listId) || !Array.isArray(c.selectedVariantIds) || c.selectedVariantIds.length > 500 || c.selectedVariantIds.some(id => !validId(id)))) throw invalid();
  return record;
};
export const recoveryExpired = record => Date.now() - record.updatedAt > RECOVERY_MAX_AGE;

export const writeCreationRecovery = (uid, country, previous, next) => {
  const existing = readCreationRecovery(uid, country);
  if (existing?.productId !== previous?.productId || existing?.revision !== previous?.revision) throw new Error('另一分頁已更新保存紀錄，請重新整理後確認同一商品');
  const record = { version: 1, uid, country, productId: next.productId, phase: next.phase,
    choices: next.choices.map(choice => ({ listId: choice.listId, selectedVariantIds: [...new Set(choice.selectedVariantIds)] })),
    updatedAt: Date.now(), revision: (previous?.revision || 0) + 1 };
  if (!validId(record.productId)) throw invalid();
  const encoded = JSON.stringify(record);
  try {
    localStorage.setItem(recoveryKey(uid, country), encoded);
    if (localStorage.getItem(recoveryKey(uid, country)) !== encoded) throw new Error();
  } catch (_) { throw new Error('無法保留商品恢復紀錄，已暫停後續保存。請保留此頁並確認瀏覽器儲存空間。'); }
  return record;
};

export const clearCreationRecovery = (uid, country, previous) => {
  const current = readCreationRecovery(uid, country);
  if (current?.productId !== previous?.productId || current?.revision !== previous?.revision) throw new Error('保存紀錄已改變，請重新整理');
  localStorage.removeItem(recoveryKey(uid, country));
};

const prepRecoveryKey = (uid, country, productId) => {
  if (!validId(productId)) throw invalid();
  return `${recoveryKey(uid, country)}:saved-prep:${encodeURIComponent(productId)}`;
};
export const readProductPrepRecovery = (uid, country, productId) => {
  const raw = localStorage.getItem(prepRecoveryKey(uid, country, productId));
  if (!raw) return null;
  let record;
  try { record = JSON.parse(raw); } catch (_) { throw invalid(); }
  if (record.version !== 1 || record.uid !== uid || record.country !== country || record.productId !== productId || record.phase !== 'saved'
    || !Number.isSafeInteger(record.revision) || !Array.isArray(record.choices) || record.choices.length > 100
    || record.choices.some(c => !validId(c.listId) || !Array.isArray(c.selectedVariantIds) || c.selectedVariantIds.length > 500 || c.selectedVariantIds.some(id => !validId(id)))) throw invalid();
  return record;
};
// Copy remaining work first, verify it, then release only this confirmed create.
// Unknown writes remain in the original slot and can never be parked as saved.
export const parkSavedCreation = (uid, country, previous) => {
  if (!previous || !['saved', 'complete'].includes(previous.phase)) throw new Error('商品尚未確認保存，不能開始另一筆');
  const current = readCreationRecovery(uid, country);
  if (current?.productId !== previous.productId || current?.revision !== previous.revision) throw new Error('保存紀錄已改變，請重新查證');
  if (previous.choices.length) {
    const existing = readProductPrepRecovery(uid, country, previous.productId);
    if (existing && JSON.stringify(existing.choices) !== JSON.stringify(previous.choices)) throw new Error('商品的待套用備播已改變，請到商品詳情核對');
    if (!existing) {
      const key = prepRecoveryKey(uid, country, previous.productId), encoded = JSON.stringify({ ...previous, phase: 'saved' });
      localStorage.setItem(key, encoded);
      if (localStorage.getItem(key) !== encoded) throw new Error('未能保留待套用備播，請保留此頁重試');
    }
  }
  clearCreationRecovery(uid, country, previous);
};
export const updateProductPrepRecovery = (uid, country, previous, choices) => {
  const current = readProductPrepRecovery(uid, country, previous.productId);
  if (current?.revision !== previous.revision) throw new Error('待套用備播已改變，請重新開啟商品詳情核對');
  const key = prepRecoveryKey(uid, country, previous.productId);
  if (!choices.length) { localStorage.removeItem(key); return null; }
  const next = { ...previous, choices: choices.map(c => ({ listId: c.listId, selectedVariantIds: [...c.selectedVariantIds] })), revision: previous.revision + 1, updatedAt: Date.now() };
  const encoded = JSON.stringify(next); localStorage.setItem(key, encoded);
  if (localStorage.getItem(key) !== encoded) throw new Error('未能更新待套用備播，請保留此頁重試');
  return next;
};

// Account changes discard selections but keep a minimal account-scoped ID
// tombstone. Returning to that account must still verify an uncertain write.
export const scrubCreationSelections = uid => {
  if (!uid) return;
  for (const country of VALID_COUNTRY_IDS) {
    try {
      const record = readCreationRecovery(uid, country);
      if (record && ['saved', 'complete'].includes(record.phase)) parkSavedCreation(uid, country, record);
      else if (record?.choices.length) writeCreationRecovery(uid, country, record, { ...record, choices: [] });
    } catch (_) { /* Keep unrecognizable records blocked, never erase an ID. */ }
  }
};

export const withCreationLock = (uid, country, work) => {
  if (!navigator.locks?.request) throw new Error('此瀏覽器無法鎖定保存流程，已暫停新增以避免重複商品。請使用新版瀏覽器。');
  return navigator.locks.request(recoveryKey(uid, country), { ifAvailable: true }, lock => {
    if (!lock) throw new Error('另一分頁正在保存此國家的商品，請待完成後重新整理');
    return work();
  });
};
